import React, { useEffect, useState } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import "bootstrap/dist/css/bootstrap.min.css";

function AllVehicleList() {
    const [vehicles, setVehicles] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 25;

    // Format date
    const formatOnlyDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const yyyy = date.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    // Format time
    const formatOnlyTime = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");
        return `${hh}:${min}`;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BASE_API_URL}/vehicle/api/allVehicleList`
                );

                const dayParking = res.data.dayParking.map(v => ({
                    ...v,
                    parkingType: "Day",
                    closed: false // day parking never has closed state
                }));

                const hourlyParking = res.data.hourlyParking.map(v => ({
                    ...v,
                    parkingType: "Hourly",
                    closed: false
                }));

                const monthlyParking = res.data.monthlyParking.map(v => ({
                    ...v,
                    parkingType: "Monthly",
                    closed: v.closed || false
                }));

                const allData = [...dayParking, ...hourlyParking, ...monthlyParking];

                const formattedData = allData.map(item => ({
                    customerName: item.customerName ? item.customerName.toUpperCase() : "-",
                    contactNumber: item.contactNumber || "-",
                    email: item.email || "-",
                    vehicleNumber: item.vehicleNumber ? item.vehicleNumber.toUpperCase() : "-",
                    vehicleType: item.vehicleType ? item.vehicleType.toUpperCase() : "-",
                    entryDate: formatOnlyDate(item.entryDate),
                    entryTime: formatOnlyTime(item.entryDate),
                    bookedDate: formatOnlyDate(item.bookedDate),
                    parkingSlot: item.parkingSlot ? item.parkingSlot.toUpperCase() : "-",
                    parkingType: item.parkingType,
                    closed: item.closed ? true : false
                }));

                setVehicles(formattedData);
            } catch (err) {
                console.error("❌ Error fetching vehicles:", err);
            }
        };

        fetchData();
    }, []);

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = vehicles.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(vehicles.length / rowsPerPage);

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="fw-bold">All Vehicle List</h2>

                <CSVLink
                    data={vehicles}
                    filename={"all-vehicles.csv"}
                    className="btn btn-success"
                >
                    Export CSV
                </CSVLink>
            </div>

            <div className="table-responsive shadow-sm">
                <table className="table table-striped table-bordered table-hover align-middle">
                    <thead className="table-dark">
                        <tr>
                            <th>Customer Name</th>
                            <th>Contact Number</th>
                            <th>Email</th>
                            <th>Vehicle Number</th>
                            <th>Vehicle Type</th>
                            <th>Entry Date</th>
                            <th>Entry Time</th>
                            <th>Booked Date</th>
                            <th>Parking Slot</th>
                            <th>Parking Type</th>
                            <th>Status</th> {/* ✅ NEW COLUMN */}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.length > 0 ? (
                            currentRows.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{item.customerName}</td>
                                    <td>{item.contactNumber}</td>
                                    <td>{item.email}</td>
                                    <td>{item.vehicleNumber}</td>
                                    <td>{item.vehicleType}</td>
                                    <td>{item.entryDate}</td>
                                    <td>{item.entryTime}</td>
                                    <td>{item.bookedDate}</td>
                                    <td>{item.parkingSlot}</td>
                                    <td>
                                        <span
                                            className={`badge ${item.parkingType === "Day"
                                                    ? "bg-primary"
                                                    : item.parkingType === "Hourly"
                                                        ? "bg-warning text-dark"
                                                        : "bg-success"
                                                }`}
                                        >
                                            {item.parkingType}
                                        </span>
                                    </td>

                                    {/* ✅ STATUS COLUMN */}
                                    <td>
                                        {item.closed ? (
                                            <span className="badge bg-danger">CLOSED</span>
                                        ) : (
                                            <span className="badge bg-success">ACTIVE</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="11" className="text-center text-muted">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-center mt-3">
                <nav>
                    <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 && "disabled"}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            >
                                Prev
                            </button>
                        </li>

                        <li className="page-item disabled">
                            <span className="page-link">
                                Page {currentPage} of {totalPages}
                            </span>
                        </li>

                        <li className={`page-item ${currentPage === totalPages && "disabled"}`}>
                            <button
                                className="page-link"
                                onClick={() =>
                                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                }
                            >
                                Next
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
}

export default AllVehicleList;
